// src/components/InstaLoan.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './InstaLoan.css';
import Calculator from '../common/calculator';

const InstaLoan = () => {
  const navigate = useNavigate();
  const [loanData, setLoanData] = useState({
    loanAmount: 50000,
    interestRate: 12.5,
    tenure: 12,
    creditScore: 750
  });
  const [emiResult, setEmiResult] = useState(null);
  const [userDetails, setUserDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    monthlyIncome: '',
    employmentType: 'salaried'
  });
  const [isApplying, setIsApplying] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const calculatorRef = useRef(null);
  const companyInfoRef = useRef(null);
  const featuresRef = useRef(null);
  const processRef = useRef(null);
  const eligibilityRef = useRef(null);

  const handleGoBack = () => {
    navigate('/home');
  };



  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };




  const calculateEMI = () => {
    const { loanAmount, interestRate, tenure } = loanData;
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure;

    const emi = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months) /
      (Math.pow(1 + monthlyRate, months) - 1);

    const totalAmount = emi * months;
    const interestPayable = totalAmount - loanAmount;

    setEmiResult({
      emi: Math.round(emi),
      totalAmount: Math.round(totalAmount),
      interestPayable: Math.round(interestPayable),
      principal: loanAmount
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanData]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleApplyNow = () => {
    setIsApplying(true);
    setTimeout(() => {
      alert('Application submitted successfully! Our executive will contact you shortly.');
      setIsApplying(false);
    }, 1500);
  };

  const handleLoanDataChange = (field, value) => {
    setLoanData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreditScoreChange = (score) => {
    let newInterestRate;
    if (score >= 800) newInterestRate = 10.5;
    else if (score >= 750) newInterestRate = 12.5;
    else if (score >= 700) newInterestRate = 14.5;
    else if (score >= 650) newInterestRate = 16.5;
    else newInterestRate = 18.5;

    setLoanData(prev => ({
      ...prev,
      creditScore: score,
      interestRate: newInterestRate
    }));
  };

  const loanFeatures = [
    {
      icon: "⚡",
      title: "Instant Approval",
      description: "Get approval within 30 minutes",
      color: "#667eea"
    },
    {
      icon: "📄",
      title: "Minimal Documents",
      description: "Only PAN, Aadhaar & Bank Statement",
      color: "#764ba2"
    },
    {
      icon: "💳",
      title: "Flexible Tenure",
      description: "3 months to 60 months tenure",
      color: "#06D6A0"
    },
    {
      icon: "🏦",
      title: "No Collateral",
      description: "Unsecured personal loans",
      color: "#FFD166"
    },
    {
      icon: "💰",
      title: "Lowest Interest",
      description: "Starting from 10.5% p.a.",
      color: "#FF6B6B"
    },
    {
      icon: "🔄",
      title: "Easy Repayment",
      description: "Flexible EMI options",
      color: "#4ECDC4"
    }
  ];

  const eligibilityOptions = [
    {
      icon: "👤",
      title: "Age",
      value: "21 - 60 years",
      description: "Minimum age requirement"
    },
    {
      icon: "💰",
      title: "Monthly Income",
      value: "₹15,000+",
      description: "For salaried individuals"
    },
    {
      icon: "📊",
      title: "Credit Score",
      value: "650+",
      description: "Minimum CIBIL score"
    },
    {
      icon: "🏢",
      title: "Employment",
      value: "3+ months",
      description: "Current employment duration"
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Apply Online",
      description: "Fill simple application form",
      icon: "📝"
    },
    {
      step: 2,
      title: "Upload Documents",
      description: "Upload required documents digitally",
      icon: "📤"
    },
    {
      step: 3,
      title: "Verification",
      description: "Quick verification process",
      icon: "✅"
    },
    {
      step: 4,
      title: "Get Money",
      description: "Amount credited to your account",
      icon: "💰"
    }
  ];

  const companyInfo = [
    {
      title: "About Krishna Finconcept Services",
      content: "Krishna Finconcept Services is a leading financial services provider committed to delivering innovative lending solutions. With over a decade of experience, we have helped millions of Indians achieve their financial goals through transparent, fast, and reliable loan services.",
      stats: ["10+ Years Experience", "2M+ Happy Customers", "₹500Cr+ Disbursed", "98% Satisfaction"]
    },
    {
      title: "Our Mission & Vision",
      content: "Our mission is to make financial services accessible to every Indian through technology-driven solutions. We envision a financially inclusive India where everyone can access credit when they need it, without unnecessary hurdles or delays.",
      points: ["Financial Inclusion", "Technology Innovation", "Customer-Centric Approach", "Transparent Operations"]
    },
    {
      title: "Why Choose Us",
      content: "We stand out in the financial services industry through our commitment to customer satisfaction, innovative products, and ethical business practices. Our partnerships with leading banks and NBFCs ensure you get the best rates and service.",
      advantages: ["Best Interest Rates", "Quick Processing", "No Hidden Charges", "24/7 Support"]
    }
  ];

  const handleScrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="instaloan-container">
      {/* Animated Background Elements */}
      <div className="instaloan-bg-animation">
        <div className="instaloan-bg-circle-1"></div>
        <div className="instaloan-bg-circle-2"></div>
        <div className="instaloan-bg-circle-3"></div>
        <div className="instaloan-bg-particles"></div>
      </div>

      {/* Progress Bar */}
      <div className="instaloan-progress-bar">
        <div
          className="instaloan-progress-fill"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>



      {/* Hero Section */}
      <section className="instaloan-hero">
        {/* Centered Title Section */}
        <div className="instaloan-hero-title-container">
          <h1 className="instaloan-hero-title">
            Instant Personal Loans
          </h1>
          <p className="instaloan-hero-subtitle">
            Get up to ₹10 Lakh as a loan in minutes!
          </p>
        </div>

        {/* Left Side: Image */}
        <div className="instaloan-hero-left">
          <div className="instaloan-hero-image-container">
            <img
              src="/images/instaloan.png"
              alt="Instant Loan Success"
              className="instaloan-hero-image"
            />
            <div className="instaloan-success-badge">
              <div className="instaloan-success-icon">✓</div>
              <div className="instaloan-success-text">
                <div className="instaloan-success-title">Successfully</div>
                <div className="instaloan-success-subtitle">Amount Credited</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="instaloan-hero-right">
          <div className="instaloan-hero-text">
            <p className="instaloan-hero-desc">
              Loans from top lenders in one place<br />
              See loan offers - no credit score impact
            </p>

            {/* Mobile Input Section */}
            <div className="instaloan-mobile-input-section">
              {/* Row 1: Full Name & PAN Number */}
              <div className="instaloan-form-row">
                <div className="instaloan-form-group">
                  <label className="instaloan-form-label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    className="instaloan-form-input"
                    placeholder="Enter your full name"
                    value={userDetails.fullName}
                    onChange={(e) => setUserDetails(prev => ({ ...prev, fullName: e.target.value }))}
                  />
                </div>

                <div className="instaloan-form-group">
                  <label className="instaloan-form-label">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    className="instaloan-form-input"
                    placeholder="Enter PAN number"
                    maxLength="10"
                    value={userDetails.panNumber || ''}
                    onChange={(e) => setUserDetails(prev => ({ ...prev, panNumber: e.target.value.toUpperCase() }))}
                  />
                </div>
              </div>

              {/* Row 2: Mobile Number & Email */}
              <div className="instaloan-form-row">
                <div className="instaloan-form-group">
                  <label className="instaloan-form-label">
                    Mobile Number
                  </label>
                  <div className="instaloan-mobile-input-wrapper">
                    <div className="instaloan-country-code">
                      <span className="instaloan-flag">🇮🇳</span>
                      <span>+91</span>
                    </div>
                    <input
                      type="tel"
                      className="instaloan-form-input instaloan-mobile-input"
                      placeholder="Enter mobile number"
                      maxLength="10"
                      value={userDetails.phone}
                      onChange={(e) => setUserDetails(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="instaloan-form-group">
                  <label className="instaloan-form-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="instaloan-form-input"
                    placeholder="Enter email address"
                    value={userDetails.email}
                    onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>



              {/* Row 3: Monthly Income & Employment Type 
        <div className="instaloan-form-row">
          <div className="instaloan-form-group">
            <label className="instaloan-form-label">
              Monthly Income
            </label>
            <div className="instaloan-income-input-wrapper">
              <div className="instaloan-income-symbol">₹</div>
              <input
                type="number"
                className="instaloan-form-input instaloan-income-input"
                placeholder="Monthly income"
                value={userDetails.monthlyIncome}
                onChange={(e) => setUserDetails(prev => ({ ...prev, monthlyIncome: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="instaloan-form-group">
            <label className="instaloan-form-label">
              Employment Type
            </label>
            <div className="instaloan-employment-select-wrapper">
              <select
                className="instaloan-employment-select"
                value={userDetails.employmentType}
                onChange={(e) => setUserDetails(prev => ({ ...prev, employmentType: e.target.value }))}
              >
                <option value="salaried">Salaried</option>
                <option value="self-employed">Self Employed</option>
                <option value="business">Business Owner</option>
                <option value="freelancer">Freelancer</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
        */}

              <div className="instaloan-consent-options">

                <label className="instaloan-checkbox-label">
                  <input
                    type="checkbox"
                    className="instaloan-checkbox"
                  />
                  <span className="instaloan-checkbox-text">I consent to Fi's Privacy Policy & T&C's</span>
                </label>
              </div>

              <button className="instaloan-check-offers-btn">
                Check your Personal Loan offers
              </button>
            </div>
          </div>
        </div>
      </section>


      <section className='homeloan-section homeloan-emi-calculator'>
        <h2>Calculate Your Insta Loan EMI Online</h2>
        <Calculator />
      </section>


      {/* Feature Highlights Section */}
      <section id="features" ref={featuresRef} className="instaloan-features-highlight-section">
        <div className="instaloan-section-header">
          <h2 className="instaloan-section-title">Choose Krishna Finconcept Services for your Personal Loan needs</h2>
          <p className="instaloan-section-subtitle">Fast, reliable, & secure online loans</p>
        </div>

        <div className="instaloan-features-highlight-grid">
          {/* Feature 1 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">No collateral required</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">📄</div>
          </div>

          {/* Feature 2 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">Interest rates start at 12.75% p.a</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">📈</div>
          </div>

          {/* Feature 3 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">50+ regulated lending partners</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">🤝</div>
          </div>

          {/* Feature 4 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">Get small loans as low as ₹10,000</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">💰</div>
          </div>

          {/* Feature 5 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">No preclosure fees on select lenders</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">📝</div>
          </div>

          {/* Feature 6 */}
          <div className="instaloan-feature-highlight-card">
            <div className="instaloan-feature-highlight-content">
              <h3 className="instaloan-feature-highlight-title">Flexible tenure up to 48 months</h3>
            </div>
            <div className="instaloan-feature-highlight-icon">📅</div>
          </div>
        </div>
      </section>



      {/* Eligibility Criteria Section (from image) */}
      <section className="instaloan-eligibility-criteria-section">
        <div className="instaloan-section-header">
          <h2 className="instaloan-section-title">Who can get an Instant Loan?</h2>
          <p className="instaloan-section-subtitle">
            Need a loan? Check your personal loan eligibility and apply online if you match the following criteria:
          </p>
        </div>

        <div className="instaloan-eligibility-criteria-grid">
          {/* Age Criteria */}
          <div className="instaloan-eligibility-criteria-card instaloan-age-card">
            <div className="instaloan-criteria-icon">👤</div>
            <div className="instaloan-criteria-content">
              <h3 className="instaloan-criteria-title">Age</h3>
              <p className="instaloan-criteria-value">21 - 60 years</p>
            </div>
          </div>

          {/* Credit Score Criteria */}
          <div className="instaloan-eligibility-criteria-card instaloan-credit-card">
            <div className="instaloan-criteria-icon">📊</div>
            <div className="instaloan-criteria-content">
              <h3 className="instaloan-criteria-title">Credit Score</h3>
              <p className="instaloan-criteria-value">700+</p>
            </div>
          </div>

          {/* Monthly Income Criteria */}
          <div className="instaloan-eligibility-criteria-card instaloan-income-card">
            <div className="instaloan-criteria-icon">💰</div>
            <div className="instaloan-criteria-content">
              <h3 className="instaloan-criteria-title">Monthly Income</h3>
              <p className="instaloan-criteria-value">Minimum ₹20,000</p>
            </div>
          </div>

          {/* Documents Criteria */}
          <div className="instaloan-eligibility-criteria-card instaloan-docs-card">
            <div className="instaloan-criteria-icon">📄</div>
            <div className="instaloan-criteria-content">
              <h3 className="instaloan-criteria-title">Documents</h3>
              <p className="instaloan-criteria-value">PAN, Aadhaar & Income Proof</p>
            </div>
          </div>
        </div>
      </section>





      {/* How to Get Section */}
      <section className="instaloan-how-to-section">
        <div className="instaloan-section-header">
          <h2 className="instaloan-section-title">How to Get Instant Personal Loans</h2>
          <p className="instaloan-section-subtitle">Complete guide to getting your loan online</p>
        </div>

        <div className="instaloan-how-to-content">
          <div className="instaloan-how-to-main">
            <h3>How to Get An Instant Personal Loan Online?</h3>
            <p>
              To get an instant personal loan online, you need to submit the required documents along with your application.
              The lender will verify and approve your request. Once approved, the loan amount is credited to your bank account.
              To complete the process, submit an online or instant personal loan application through the lender's platform.
              You must then repay the loan through EMIs (Equated Monthly Instalments) over the selected repayment tenure.
            </p>
          </div>

          <div className="instaloan-how-to-tips">
            <h3>How to Get Low Interest Personal Loans?</h3>
            <p>When you are about to apply for a personal loan online, there are certain tips that can prove to be helpful in availing a lower rate of interest:</p>
          </div>
        </div>
      </section>






      {/* FAQ Section */}
      <section className="instaloan-faq-section">
        <div className="instaloan-section-header">
          <h2 className="instaloan-section-title">Frequently asked questions (FAQs)</h2>
          <p className="instaloan-section-subtitle">Find answers to common questions about personal loans</p>
        </div>

        <div className="instaloan-faq-container">
          {/* FAQ 1 */}
          <div
            className={`instaloan-faq-item ${activeFaq === 0 ? 'active' : ''}`}
            onClick={() => toggleFaq(0)}
          >
            <div className="instaloan-faq-question">
              <h3>What is a personal loan and how does it work?</h3>
              <span className="instaloan-faq-icon">+</span>
            </div>
            <div className="instaloan-faq-answer">
              <p>
                A personal loan is an unsecured loan that you can use for various personal needs like
                medical emergencies, home renovation, travel, or debt consolidation. The loan amount is
                disbursed as a lump sum, and you repay it in fixed monthly installments (EMIs) over a
                predetermined tenure, along with interest charged on the principal amount.
              </p>
            </div>
          </div>

          {/* FAQ 2 */}
          <div
            className={`instaloan-faq-item ${activeFaq === 1 ? 'active' : ''}`}
            onClick={() => toggleFaq(1)}
          >
            <div className="instaloan-faq-question">
              <h3>What is a personal loan rate of interest?</h3>
              <span className="instaloan-faq-icon">+</span>
            </div>
            <div className="instaloan-faq-answer">
              <p>
                The interest rate on a personal loan typically ranges from 10.5% to 24% per annum,
                depending on factors like your credit score, income, employment history, and the
                lender's policies. At Krishna Finconcept Services, we offer competitive rates starting
                from 10.5% for customers with excellent credit profiles.
              </p>
            </div>
          </div>

          {/* FAQ 3 */}
          <div
            className={`instaloan-faq-item ${activeFaq === 2 ? 'active' : ''}`}
            onClick={() => toggleFaq(2)}
          >
            <div className="instaloan-faq-question">
              <h3>How to get a personal loan online?</h3>
              <span className="instaloan-faq-icon">+</span>
            </div>
            <div className="instaloan-faq-answer">
              <p>
                Getting a personal loan online is simple: 1) Fill the application form with your
                personal and financial details, 2) Upload required documents (PAN, Aadhaar, income proof),
                3) Get instant approval decision, 4) Accept the loan offer, and 5) Receive funds directly
                in your bank account within 24-48 hours of approval.
              </p>
            </div>
          </div>

          {/* FAQ 4 */}
          <div
            className={`instaloan-faq-item ${activeFaq === 3 ? 'active' : ''}`}
            onClick={() => toggleFaq(3)}
          >
            <div className="instaloan-faq-question">
              <h3>When should I think of getting a personal loan?</h3>
              <span className="instaloan-faq-icon">+</span>
            </div>
            <div className="instaloan-faq-answer">
              <p>
                Consider a personal loan when you need funds for: Medical emergencies, home renovation,
                wedding expenses, education fees, debt consolidation, travel plans, or business expansion.
                It's ideal when you need quick funds without collateral and have a stable income to
                repay the EMIs comfortably.
              </p>
            </div>
          </div>

          {/* FAQ 5 */}
          <div
            className={`instaloan-faq-item ${activeFaq === 4 ? 'active' : ''}`}
            onClick={() => toggleFaq(4)}
          >
            <div className="instaloan-faq-question">
              <h3>What can I use a personal loan for?</h3>
              <span className="instaloan-faq-icon">+</span>
            </div>
            <div className="instaloan-faq-answer">
              <p>
                Personal loans can be used for various purposes including: Debt consolidation, home
                renovation, medical expenses, wedding expenses, vacation funding, education expenses,
                business needs, purchasing electronics or appliances, and emergency cash requirements.
                Unlike specific-purpose loans, personal loans offer flexibility in usage.
              </p>
            </div>
          </div>
        </div>
      </section>





    </div>
  );
};

export default InstaLoan;